/**
 * Client-side Indian stock query parser.
 *
 * No API key, no network call, zero latency.
 * Strategy: normalize query → substring-match against a comprehensive
 * company name/alias database → longest match wins.
 */

interface Entry {
  match: string;   // normalised (lowercase, no punctuation)
  ticker: string;
  weight: number;  // longer / more specific entries get higher weight naturally
}

// ─── Raw company database ─────────────────────────────────────────────────
// Each entry can be the official name, a common nickname, abbreviation,
// or colloquial form (e.g. "jockey" → PAGEIND, "fevicol" → PIDILITIND).
const RAW: [string, string][] = [
  // ── Indices ──────────────────────────────────────────────────────────────
  ['nifty 50',              '^NSEI'],
  ['nifty fifty',           '^NSEI'],
  ['nifty',                 '^NSEI'],
  ['sensex',                '^BSESN'],
  ['bse 30',                '^BSESN'],
  ['bank nifty',            '^NSEBANK'],
  ['banknifty',             '^NSEBANK'],
  ['nifty bank',            '^NSEBANK'],
  ['nifty it',              '^CNXIT'],
  ['nifty auto',            '^CNXAUTO'],
  ['nifty pharma',          '^CNXPHARMA'],
  ['nifty fmcg',            '^CNXFMCG'],
  ['nifty metal',           '^CNXMETAL'],
  ['nifty realty',          '^CNXREALTY'],
  ['nifty infra',           '^CNXINFRA'],
  ['nifty energy',          '^CNXENERGY'],
  ['india vix',             '^INDIAVIX'],
  ['vix',                   '^INDIAVIX'],
  ['midcap',                '^NSEI'],   // fallback
  ['smallcap',              '^NSEI'],

  // ── Reliance ──────────────────────────────────────────────────────────────
  ['reliance industries',   'RELIANCE'],
  ['reliance',              'RELIANCE'],
  ['ril',                   'RELIANCE'],
  ['mukesh ambani company', 'RELIANCE'],

  // ── IT ───────────────────────────────────────────────────────────────────
  ['tata consultancy services', 'TCS'],
  ['tata consultancy',      'TCS'],
  ['tcs',                   'TCS'],
  ['infosys',               'INFY'],
  ['infy',                  'INFY'],
  ['wipro',                 'WIPRO'],
  ['hcl technologies',      'HCLTECH'],
  ['hcl tech',              'HCLTECH'],
  ['hcltech',               'HCLTECH'],
  ['hcl',                   'HCLTECH'],
  ['tech mahindra',         'TECHM'],
  ['techmahindra',          'TECHM'],
  ['mphasis',               'MPHASIS'],
  ['persistent systems',    'PERSISTENT'],
  ['persistent',            'PERSISTENT'],
  ['coforge',               'COFORGE'],
  ['ltimindtree',           'LTIM'],
  ['lti mindtree',          'LTIM'],
  ['mindtree',              'LTIM'],
  ['tata elxsi',            'TATAELXSI'],
  ['happiest minds',        'HAPPSTMNDS'],
  ['tanla',                 'TANLA'],
  ['cyient',                'CYIENT'],
  ['hexaware',              'HEXAWARE'],
  ['birlasoft',             'BSOFT'],
  ['mastek',                'MASTEK'],
  ['kpit technologies',     'KPITTECH'],
  ['kpit',                  'KPITTECH'],

  // ── Banks ─────────────────────────────────────────────────────────────────
  ['hdfc bank',             'HDFCBANK'],
  ['hdfcbank',              'HDFCBANK'],
  ['hdfc',                  'HDFCBANK'],
  ['icici bank',            'ICICIBANK'],
  ['icicibsnk',             'ICICIBANK'],
  ['state bank of india',   'SBIN'],
  ['state bank',            'SBIN'],
  ['sbi',                   'SBIN'],
  ['kotak mahindra bank',   'KOTAKBANK'],
  ['kotak bank',            'KOTAKBANK'],
  ['kotak',                 'KOTAKBANK'],
  ['axis bank',             'AXISBANK'],
  ['indusind bank',         'INDUSINDBK'],
  ['indusind',              'INDUSINDBK'],
  ['bank of baroda',        'BANKBARODA'],
  ['baroda bank',           'BANKBARODA'],
  ['bob',                   'BANKBARODA'],
  ['punjab national bank',  'PNB'],
  ['pnb',                   'PNB'],
  ['canara bank',           'CANBK'],
  ['canara',                'CANBK'],
  ['union bank',            'UNIONBANK'],
  ['federal bank',          'FEDERALBNK'],
  ['yes bank',              'YESBANK'],
  ['idfc first bank',       'IDFCFIRSTB'],
  ['idfc',                  'IDFCFIRSTB'],
  ['bandhan bank',          'BANDHANBNK'],
  ['rbl bank',              'RBLBANK'],
  ['indian bank',           'INDIANB'],
  ['uco bank',              'UCOBANK'],
  ['central bank',          'CENTRALBK'],
  ['karur vysya bank',      'KARURVYSYA'],

  // ── NBFCs / Finance ───────────────────────────────────────────────────────
  ['bajaj finance',         'BAJFINANCE'],
  ['bajfinance',            'BAJFINANCE'],
  ['bajaj finserv',         'BAJAJFINSV'],
  ['shriram finance',       'SHRIRAMFIN'],
  ['muthoot finance',       'MUTHOOTFIN'],
  ['muthoot',               'MUTHOOTFIN'],
  ['manappuram',            'MANAPPURAM'],
  ['cholamandalam',         'CHOLAFIN'],
  ['chola',                 'CHOLAFIN'],
  ['pnb housing',           'PNBHOUSING'],

  // ── Insurance ────────────────────────────────────────────────────────────
  ['life insurance corporation', 'LICI'],
  ['lic',                   'LICI'],
  ['sbi life',              'SBILIFE'],
  ['hdfc life',             'HDFCLIFE'],
  ['icici prudential life', 'ICICIPRULI'],
  ['icici pru',             'ICICIPRULI'],
  ['max financial',         'MFSL'],
  ['star health',           'STARHEALTH'],
  ['niva bupa',             'NIVABUPA'],
  ['go digit',              'GODIGIT'],

  // ── Consumer / FMCG ──────────────────────────────────────────────────────
  ['hindustan unilever',    'HINDUNILVR'],
  ['hindustan lever',       'HINDUNILVR'],
  ['hul',                   'HINDUNILVR'],
  ['unilever india',        'HINDUNILVR'],
  ['nestle india',          'NESTLEIND'],
  ['nestle',                'NESTLEIND'],
  ['britannia',             'BRITANNIA'],
  ['dabur',                 'DABUR'],
  ['marico',                'MARICO'],
  ['parachute oil',         'MARICO'],
  ['godrej consumer',       'GODREJCP'],
  ['godrej',                'GODREJCP'],
  ['emami',                 'EMAMILTD'],
  ['colgate',               'COLPAL'],
  ['tata consumer products','TATACONSUM'],
  ['tata consumer',         'TATACONSUM'],
  ['asian paints',          'ASIANPAINT'],
  ['berger paints',         'BERGEPAINT'],
  ['kansai nerolac',        'KANSAINER'],
  ['pidilite',              'PIDILITIND'],
  ['fevicol',               'PIDILITIND'],  // brand → company

  // ── Auto ─────────────────────────────────────────────────────────────────
  ['maruti suzuki',         'MARUTI'],
  ['maruti',                'MARUTI'],
  ['suzuki india',          'MARUTI'],
  ['tata motors',           'TATAMOTORS'],
  ['tatamotors',            'TATAMOTORS'],
  ['mahindra and mahindra', 'M&M'],
  ['mahindra mahindra',     'M&M'],
  ['mahindra',              'M&M'],
  ['hero motocorp',         'HEROMOTOCO'],
  ['hero moto',             'HEROMOTOCO'],
  ['hero honda',            'HEROMOTOCO'],
  ['hero',                  'HEROMOTOCO'],
  ['bajaj auto',            'BAJAJ-AUTO'],
  ['bajaj',                 'BAJAJ-AUTO'],
  ['eicher motors',         'EICHERMOT'],
  ['eicher',                'EICHERMOT'],
  ['royal enfield',         'EICHERMOT'],  // brand → company
  ['tvs motor',             'TVSMOTOR'],
  ['tvs',                   'TVSMOTOR'],
  ['ashok leyland',         'ASHOKLEY'],
  ['ola electric',          'OLAELEC'],
  ['ola',                   'OLAELEC'],

  // ── Pharma ───────────────────────────────────────────────────────────────
  ['sun pharma',            'SUNPHARMA'],
  ['sun pharmaceutical',    'SUNPHARMA'],
  ['dr reddys',             'DRREDDY'],
  ['dr reddy',              'DRREDDY'],
  ['cipla',                 'CIPLA'],
  ['divi labs',             'DIVISLAB'],
  ['divis lab',             'DIVISLAB'],
  ['divi',                  'DIVISLAB'],
  ['lupin',                 'LUPIN'],
  ['torrent pharma',        'TORNTPHARM'],
  ['torrent pharmaceuticals','TORNTPHARM'],
  ['biocon',                'BIOCON'],
  ['alkem',                 'ALKEM'],
  ['abbott india',          'ABBOTINDIA'],
  ['pfizer india',          'PFIZER'],
  ['gland pharma',          'GLAND'],
  ['ipca',                  'IPCALAB'],
  ['natco pharma',          'NATCOPHARM'],
  ['mankind pharma',        'MANKIND'],
  ['zydus lifesciences',    'ZYDUSLIFE'],
  ['zydus',                 'ZYDUSLIFE'],

  // ── Energy / Oil & Gas ────────────────────────────────────────────────────
  ['ongc',                  'ONGC'],
  ['oil and natural gas',   'ONGC'],
  ['bpcl',                  'BPCL'],
  ['bharat petroleum',      'BPCL'],
  ['ioc',                   'IOC'],
  ['indian oil',            'IOC'],
  ['ntpc',                  'NTPC'],
  ['power grid',            'POWERGRID'],
  ['powergrid',             'POWERGRID'],
  ['adani green',           'ADANIGREEN'],
  ['adani power',           'ADANIPOWER'],
  ['adani total gas',       'ATGL'],
  ['torrent power',         'TORNTPOWER'],
  ['jsw energy',            'JSWENERGY'],
  ['tata power',            'TATAPOWER'],
  ['gujarat gas',           'GUJGASLTD'],
  ['petronet',              'PETRONET'],
  ['gail',                  'GAIL'],

  // ── Metals & Mining ──────────────────────────────────────────────────────
  ['tata steel',            'TATASTEEL'],
  ['jsw steel',             'JSWSTEEL'],
  ['steel authority',       'SAIL'],
  ['sail',                  'SAIL'],
  ['hindalco',              'HINDALCO'],
  ['vedanta',               'VEDL'],
  ['national aluminium',    'NATIONALUM'],
  ['nalco',                 'NATIONALUM'],
  ['coal india',            'COALINDIA'],
  ['nmdc',                  'NMDC'],
  ['hindustan zinc',        'HINDZINC'],
  ['hindustan copper',      'HINDCOPPER'],
  ['moil',                  'MOIL'],

  // ── Cement ────────────────────────────────────────────────────────────────
  ['ultratech cement',      'ULTRACEMCO'],
  ['ultratech',             'ULTRACEMCO'],
  ['shree cement',          'SHREECEM'],
  ['shree',                 'SHREECEM'],
  ['ambuja cement',         'AMBUJACEM'],
  ['ambuja',                'AMBUJACEM'],
  ['acc',                   'ACC'],
  ['dalmia bharat',         'DALBHARAT'],
  ['ramco cement',          'RAMCOCEM'],
  ['jk cement',             'JKCEMENT'],

  // ── Telecom ───────────────────────────────────────────────────────────────
  ['bharti airtel',         'BHARTIARTL'],
  ['airtel',                'BHARTIARTL'],
  ['bharti',                'BHARTIARTL'],
  ['vodafone idea',         'IDEA'],
  ['vi',                    'IDEA'],
  ['idea',                  'IDEA'],
  ['jio',                   'RELIANCE'],   // Jio is Reliance
  ['tata communications',   'TATACOMM'],
  ['hfcl',                  'HFCL'],

  // ── Conglomerates & Others ────────────────────────────────────────────────
  ['adani enterprises',     'ADANIENT'],
  ['adani ports',           'ADANIPORTS'],
  ['adani',                 'ADANIENT'],
  ['larsen and toubro',     'LT'],
  ['larsen toubro',         'LT'],
  ['larsen',                'LT'],
  ['l&t',                   'LT'],
  ['grasim',                'GRASIM'],
  ['aditya birla',          'GRASIM'],
  ['titan',                 'TITAN'],
  ['tanishq',               'TITAN'],      // brand → company
  ['tata chemicals',        'TATACHEM'],
  ['trent',                 'TRENT'],
  ['westside',              'TRENT'],      // brand → company
  ['upl',                   'UPL'],
  ['siemens',               'SIEMENS'],
  ['abb',                   'ABB'],
  ['bosch',                 'BOSCHLTD'],
  ['havells',               'HAVELLS'],
  ['voltas',                'VOLTAS'],
  ['bluestar',              'BLUESTAR'],
  ['crompton',              'CROMPTON'],
  ['polycab',               'POLYCAB'],
  ['mrf',                   'MRF'],
  ['page industries',       'PAGEIND'],
  ['jockey',                'PAGEIND'],    // brand → company
  ['dixon technologies',    'DIXON'],
  ['dixon',                 'DIXON'],
  ['amber enterprises',     'AMBER'],
  ['kaynes technology',     'KAYNES'],
  ['kaynes',                'KAYNES'],

  // ── New-age / Consumer Tech ───────────────────────────────────────────────
  ['zomato',                'ZOMATO'],
  ['blinkit',               'ZOMATO'],     // brand → company
  ['swiggy',                'SWIGGY'],
  ['instamart',             'SWIGGY'],     // brand → company
  ['paytm',                 'PAYTM'],
  ['one97 communications',  'PAYTM'],
  ['nykaa',                 'NYKAA'],
  ['fsn ecommerce',         'NYKAA'],
  ['dmart',                 'DMART'],
  ['avenue supermarts',     'DMART'],
  ['indiamart',             'INDIAMART'],
  ['info edge',             'NAUKRI'],
  ['naukri',                'NAUKRI'],
  ['policybazaar',          'POLICYBZR'],
  ['pb fintech',            'POLICYBZR'],
  ['delhivery',             'DELHIVERY'],
  ['mapmyindia',            'MAPMYINDIA'],
  ['ce info systems',       'MAPMYINDIA'],
  ['physicswallah',         'PHYSICSW'],
  ['physics wallah',        'PHYSICSW'],
  ['pw edtech',             'PHYSICSW'],
  ['pw',                    'PHYSICSW'],
  ['eternal',               'ETERNAL'],
  ['mobikwik',              'MOBIKWIK'],

  // ── Defence ───────────────────────────────────────────────────────────────
  ['hal',                   'HAL'],
  ['hindustan aeronautics', 'HAL'],
  ['bel',                   'BEL'],
  ['bharat electronics',    'BEL'],
  ['irfc',                  'IRFC'],
  ['irctc',                 'IRCTC'],
  ['indian railways catering','IRCTC'],
  ['mazagon dock',          'MAZDOCK'],
  ['cochin shipyard',       'COCHINSHIP'],
  ['garden reach',          'GRSE'],
  ['bharat dynamics',       'BDL'],
  ['data patterns',         'DATAPATTNS'],

  // ── Real Estate ───────────────────────────────────────────────────────────
  ['dlf',                   'DLF'],
  ['godrej properties',     'GODREJPROP'],
  ['lodha',                 'LODHA'],
  ['prestige estates',      'PRESTIGE'],
  ['prestige',              'PRESTIGE'],
  ['oberoi realty',         'OBEROIRLTY'],
  ['brigade enterprises',   'BRIGADE'],
  ['sobha',                 'SOBHA'],
  ['phoenix mills',         'PHOENIXLTD'],

  // ── Aviation & Hospitality ────────────────────────────────────────────────
  ['interglobe aviation',   'INDIGO'],
  ['indigo airlines',       'INDIGO'],
  ['indigo',                'INDIGO'],
  ['spicejet',              'SPICEJET'],
  ['air india',             'AIRINDIA'],
  ['indian hotels',         'INDHOTEL'],
  ['taj hotels',            'INDHOTEL'],
  ['ihcl',                  'INDHOTEL'],
  ['lemon tree',            'LEMONTREE'],
  ['jubilant foodworks',    'JUBLFOOD'],
  ['dominos india',         'JUBLFOOD'],
  ['dominos',               'JUBLFOOD'],
  ['westlife development',  'WESTLIFE'],
  ['mcdonalds india',       'WESTLIFE'],
  ['varun beverages',       'VBL'],
  ['devyani international', 'DEVYANI'],
  ['restaurant brands',     'RBA'],
  ['burger king india',     'RBA'],

  // ── Agri ─────────────────────────────────────────────────────────────────
  ['pi industries',         'PIIND'],
  ['coromandel',            'COROMANDEL'],
  ['kaveri seed',           'KSCL'],
  ['rallis india',          'RALLIS'],
  ['bayer cropscience',     'BAYERCROP'],
];

// ── Build normalised entries ───────────────────────────────────────────────
function norm(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, '');
}

const ENTRIES: Entry[] = RAW.map(([match, ticker]) => ({
  match: norm(match),
  ticker,
  weight: match.length,   // longer match → higher weight
})).sort((a, b) => b.weight - a.weight);  // longest first so we match greedily

// ── Index synonym regex (fast path, runs before substring search) ──────────
const INDEX_PATTERNS: [RegExp, string][] = [
  [/\bnifty\s*50\b/i,          '^NSEI'],
  [/\bnifty\s+fifty\b/i,       '^NSEI'],
  [/\bbank\s*nifty\b/i,        '^NSEBANK'],
  [/\bbanknifty\b/i,           '^NSEBANK'],
  [/\bnifty\s*bank\b/i,        '^NSEBANK'],
  [/\bsensex\b/i,              '^BSESN'],
  [/\bbse\s*30\b/i,            '^BSESN'],
  [/\bnifty\s*it\b/i,          '^CNXIT'],
  [/\bnifty\s*auto\b/i,        '^CNXAUTO'],
  [/\bnifty\s*pharma\b/i,      '^CNXPHARMA'],
  [/\bnifty\s*fmcg\b/i,        '^CNXFMCG'],
  [/\bnifty\s*metal\b/i,       '^CNXMETAL'],
  [/\bnifty\s*realty\b/i,      '^CNXREALTY'],
  [/\bnifty\s*energy\b/i,      '^CNXENERGY'],
  [/\bindia\s*vix\b/i,         '^INDIAVIX'],
  [/\b(india\s+)?vix\b/i,      '^INDIAVIX'],
  [/\bnifty\b/i,               '^NSEI'],    // plain "nifty" = NIFTY 50
];

/**
 * Extract an NSE/BSE ticker from any natural language query.
 * Returns e.g. "RELIANCE", "^NSEI", "TATAMOTORS" etc.
 */
export function extractTickerFromQuery(query: string): string {
  // 1. Index fast path
  for (const [re, ticker] of INDEX_PATTERNS) {
    if (re.test(query)) return ticker;
  }

  // 2. Normalise the full query for substring matching
  const normQuery = norm(query);

  // 3. Find all matching entries (sorted longest-first already)
  let best: Entry | null = null;
  for (const entry of ENTRIES) {
    if (normQuery.includes(entry.match)) {
      // Prefer the entry whose normalised match is longest
      if (!best || entry.weight > best.weight) {
        best = entry;
      }
    }
  }

  if (best) return best.ticker;

  // 4. Last resort: biggest capitalised word in the original query
  //    that doesn't look like a common English word
  const SKIP = new Set([
    'HOW','HOWS','WHAT','WHATS','WHY','WHEN','WHERE','WHO',
    'IS','ARE','WAS','WERE','DOES','DO','DID','HAS','HAD',
    'CAN','COULD','SHOULD','WOULD','WILL','SHALL',
    'THE','A','AN','IN','ON','AT','BY','FOR','TO','OF','FROM',
    'WITH','AND','OR','BUT','NOT','IT','ITS','THIS','THAT',
    'ABOUT','GIVE','TELL','SHOW','FIND','GET','LOOK','THINK',
    'GOING','DOING','GOOD','BEST','TOP','BUY','SELL','HOLD',
    'STOCK','MARKET','SHARE','SECTOR','INDEX','TREND','PRICE',
    'NEWS','DATA','ANALYSIS','CHART','TODAY','WEEK','MONTH','YEAR',
  ]);
  const words = query.toUpperCase().match(/\b[A-Z]{3,12}\b/g) ?? [];
  const candidates = words.filter(w => !SKIP.has(w));
  if (candidates.length > 0) {
    return candidates.reduce((a, b) => (b.length > a.length ? b : a));
  }

  return '^NSEI'; // default: NIFTY 50
}

/** Human-readable label for a ticker symbol */
export function tickerLabel(ticker: string): string {
  const INDEX_LABELS: Record<string, string> = {
    '^NSEI':     'NIFTY 50',
    '^BSESN':    'SENSEX',
    '^NSEBANK':  'BANK NIFTY',
    '^CNXIT':    'NIFTY IT',
    '^CNXAUTO':  'NIFTY AUTO',
    '^CNXPHARMA':'NIFTY PHARMA',
    '^CNXFMCG':  'NIFTY FMCG',
    '^CNXMETAL': 'NIFTY METAL',
    '^CNXREALTY':'NIFTY REALTY',
    '^CNXENERGY':'NIFTY ENERGY',
    '^INDIAVIX': 'INDIA VIX',
  };
  return INDEX_LABELS[ticker] ?? ticker;
}
